/**
 * EventLog
 *
 * Camera event history for a specific NVR. Accepts nvrId prop and reads
 * events from nvrs[nvrId].events in the store.
 *
 * Features:
 * - Filter tabs: All / Motion / Person / Vehicle / Animal / Plate / Ring
 * - Lazy-loaded thumbnails from the backend
 * - Recording clip download button
 * - License plate events highlighted
 */

import React, { useEffect, useState, useCallback } from 'react';
import { UnifiEvent, useUnifiProtectStore, useNvrEvents } from '../unifi-protect-store';

interface EventLogProps {
  nvrId: string;
}

// ── Filter definitions ────────────────────────────────────────────────────────

type FilterKey = 'all' | 'motion' | 'person' | 'vehicle' | 'animal' | 'licensePlate' | 'ring';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all',          label: 'All' },
  { key: 'motion',       label: 'Motion' },
  { key: 'person',       label: '🧍 Person' },
  { key: 'vehicle',      label: '🚗 Vehicle' },
  { key: 'animal',       label: '🐾 Animal' },
  { key: 'licensePlate', label: '🪪 Plates' },
  { key: 'ring',         label: '🔔 Ring' },
];

function matchesFilter(event: UnifiEvent, filter: FilterKey): boolean {
  if (filter === 'all') return true;
  if (filter === 'motion') return event.type === 'motion';
  if (filter === 'ring') return event.type === 'ring';
  if (filter === 'person') return event.smartDetectTypes?.includes('person') ?? false;
  if (filter === 'vehicle') return event.smartDetectTypes?.includes('vehicle') ?? false;
  if (filter === 'animal') return event.smartDetectTypes?.includes('animal') ?? false;
  if (filter === 'licensePlate') return event.smartDetectTypes?.includes('licensePlate') ?? false;
  return true;
}

function formatTime(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function eventIcon(event: UnifiEvent): string {
  if (event.type === 'ring') return '🔔';
  if (event.type === 'audioDetect') return '🔊';
  if (event.smartDetectTypes?.includes('licensePlate')) return '🪪';
  if (event.smartDetectTypes?.includes('person')) return '🧍';
  if (event.smartDetectTypes?.includes('vehicle')) return '🚗';
  if (event.smartDetectTypes?.includes('animal')) return '🐾';
  if (event.smartDetectTypes?.includes('package')) return '📦';
  if (event.type === 'motion') return '👁️';
  return '📷';
}

function eventLabel(event: UnifiEvent): string {
  if (event.type === 'ring') return 'Doorbell Ring';
  if (event.type === 'audioDetect') return event.audioType ? `Sound: ${event.audioType}` : 'Audio Detected';
  if (event.type === 'smartDetectZone') {
    const types = event.smartDetectTypes ?? [];
    return types.length > 0
      ? types.map(t => t === 'licensePlate' ? 'License Plate' : t.charAt(0).toUpperCase() + t.slice(1)).join(', ') + ' Detected'
      : 'Smart Detection';
  }
  return 'Motion';
}

function eventBadgeColor(event: UnifiEvent): string {
  if (event.type === 'ring') return '#3b82f6';
  if (event.type === 'audioDetect') return '#8b5cf6';
  if (event.smartDetectTypes?.includes('licensePlate')) return '#f59e0b';
  if (event.type === 'smartDetectZone') return '#22c55e';
  return '#f59e0b';
}

const isLpEvent = (e: UnifiEvent) => e.smartDetectTypes?.includes('licensePlate') ?? false;

// ── Thumbnail component ───────────────────────────────────────────────────────

function EventThumbnail({ nvrId, eventId }: { nvrId: string; eventId: string }) {
  const fetchEventThumbnail = useUnifiProtectStore(s => s.fetchEventThumbnail);
  const cached = useUnifiProtectStore(s => s.nvrs[nvrId]?.eventThumbnails[eventId]);
  const [url, setUrl] = useState<string | null>(cached ?? null);
  const [tried, setTried] = useState(!!cached);

  useEffect(() => {
    if (tried) return;
    setTried(true);
    fetchEventThumbnail(nvrId, eventId).then(u => setUrl(u));
  }, [nvrId, eventId, fetchEventThumbnail, tried]);

  if (!url) {
    return (
      <div style={{
        width: 56, height: 40, flexShrink: 0,
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, color: 'hsl(var(--muted-foreground))',
      }}>
        📷
      </div>
    );
  }

  return (
    <img
      src={url}
      alt="event thumbnail"
      style={{ width: 56, height: 40, flexShrink: 0, objectFit: 'cover', borderRadius: 6 }}
    />
  );
}

// ── Clip download helper ──────────────────────────────────────────────────────

function downloadClip(nvrId: string, event: UnifiEvent) {
  const start = (event.start ?? Date.now()) - 10_000;
  const end   = (event.end ?? event.start ?? Date.now()) + 10_000;
  const url = `/api/v1/integrations/unifi-protect/nvrs/${nvrId}/cameras/${encodeURIComponent(event.cameraId)}/recording?start=${start}&end=${end}&channel=0`;
  const a = document.createElement('a');
  a.href = url;
  a.download = `clip-${event.cameraId}-${event.start}.mp4`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ── Main component ────────────────────────────────────────────────────────────

export function EventLog({ nvrId }: EventLogProps) {
  const events = useNvrEvents(nvrId);
  const isConnected = useUnifiProtectStore(s => s.nvrs[nvrId]?.isConnected ?? false);
  const loadEvents = useUnifiProtectStore(s => s.loadEvents);

  const [filter, setFilter] = useState<FilterKey>('all');

  useEffect(() => {
    if (isConnected) {
      loadEvents(nvrId);
    }
  }, [isConnected, nvrId, loadEvents]);

  const filtered = events.filter(e => matchesFilter(e, filter));

  const handleFilterClick = useCallback((key: FilterKey) => {
    setFilter(key);
  }, []);

  return (
    <div style={{
      background: 'hsl(var(--card))',
      border: '1px solid hsl(var(--border))',
      borderRadius: 12,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 520,
    }}>
      {/* Header */}
      <div style={{ padding: '14px 18px 0', borderBottom: '1px solid hsl(var(--border))', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'hsl(var(--foreground))' }}>
            Events
          </h3>
          {filtered.length > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px',
              borderRadius: 10, background: 'rgba(255,255,255,0.1)',
              color: 'hsl(var(--muted-foreground))',
            }}>
              {filtered.length}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 10 }}>
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => handleFilterClick(f.key)}
              style={{
                padding: '4px 10px',
                borderRadius: 20,
                border: `1px solid ${filter === f.key ? 'rgba(99,102,241,0.5)' : 'hsl(var(--border))'}`,
                background: filter === f.key ? 'rgba(99,102,241,0.15)' : 'transparent',
                color: filter === f.key ? '#818cf8' : 'hsl(var(--muted-foreground))',
                fontSize: 11,
                fontWeight: filter === f.key ? 700 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'all 0.15s ease',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Event list */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: 14 }}>
            {events.length === 0
              ? 'No events yet. Events will appear here in real time.'
              : 'No events match this filter.'}
          </div>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {filtered.map((event, i) => {
              const isLp = isLpEvent(event);
              return (
                <li
                  key={event.id || i}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid hsl(var(--border))',
                    borderLeft: isLp ? '3px solid #f59e0b' : '3px solid transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: isLp ? 'rgba(245,158,11,0.05)' : 'transparent',
                  }}
                >
                  <EventThumbnail nvrId={nvrId} eventId={event.id} />
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{eventIcon(event)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      color: eventBadgeColor(event),
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {eventLabel(event)}
                    </div>
                    <div style={{
                      fontSize: 11, color: 'hsl(var(--muted-foreground))', marginTop: 2,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {event.cameraName || event.cameraId}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                    <div style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))' }}>
                      {formatTime(event.start)}
                    </div>
                    {event.score !== undefined && (
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>
                        {event.score}%
                      </div>
                    )}
                    {event.cameraId && (
                      <button
                        onClick={() => downloadClip(nvrId, event)}
                        title="Download clip"
                        style={{
                          padding: '2px 6px',
                          fontSize: 10,
                          fontWeight: 600,
                          background: 'rgba(255,255,255,0.07)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          borderRadius: 4,
                          color: 'hsl(var(--muted-foreground))',
                          cursor: 'pointer',
                        }}
                      >
                        ⬇ Clip
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
